import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import httpRequest from "../../services/httpRequest";
import AppUtil from "utilities/AppUtil";

const _urlPrefix = "/goal-tracker";

export const fetchClientGoals = createAsyncThunk(
  "goalTracker/fetchClientGoals",
  async (params) => {
    const { clientId, page = 1, size = 10 } = params;
    const result = await httpRequest({
      url: _urlPrefix,
      method: "GET",
      params: AppUtil.toSnakeCaseKey({ clientId, page, size }),
    });
    if (!result) return;
    return result.data;
  }
);

export const createClientGoal = createAsyncThunk(
  "goalTracker/createClientGoal",
  async (payload) => {
    const result = await httpRequest({
      url: _urlPrefix,
      method: "POST",
      data: AppUtil.toSnakeCaseKey(payload),
    });
    return result;
  }
);

export const updateClientGoal = createAsyncThunk(
  "goalTracker/updateClientGoal",
  async ({ id, payload }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(payload),
    });
    return result;
  }
);

export const updateGoalStatus = createAsyncThunk(
  "goalTracker/updateGoalStatus",
  async ({ id, payload }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/status`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(payload),
    });
    return result;
  }
);

export const deleteGoalTracker = createAsyncThunk(
  "goalTracker/deleteGoalTracker",
  async (id) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}`,
      method: "DELETE",
    });
    return result;
  }
);

export const updateMilestoneStatus = createAsyncThunk(
  "goalTracker/updateMilestoneStatus",
  async ({ milestoneId, isCompleted }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/milestone/${milestoneId}`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey({ isCompleted }),
    });
    return result;
  }
);

export const fetchGoalAnswers = createAsyncThunk(
  "goalTracker/fetchGoalAnswers",
  async (params) => {
    const { goalTrackerId, page, size } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${goalTrackerId}/answers`,
      method: "GET",
      params: AppUtil.toSnakeCaseKey({ page, size }),
    });
    if (!result) return;
    return {
      goalTrackerId,
      data: result.data
    };
  }
);

const initialState = {
  goalsPage: null,
  answersByGoalTrackerId: {},
  loadingGoals: false,
  loadingAnswers: false,
};

const goalTracker = createSlice({
  name: "goalTracker",
  initialState,
  reducers: {},
  extraReducers: {
    [fetchClientGoals.pending]: (state) => {
      state.loadingGoals = true;
    },
    [fetchClientGoals.fulfilled]: (state, { payload }) => {
      state.loadingGoals = false;
      if (payload) {
        state.goalsPage = payload;
      }
    },
    [fetchClientGoals.rejected]: (state) => {
      state.loadingGoals = false;
    },
    [fetchGoalAnswers.pending]: (state) => {
      state.loadingAnswers = true;
    },
    [fetchGoalAnswers.fulfilled]: (state, { payload }) => {
      state.loadingAnswers = false;
      if (payload && payload.data) {
        state.answersByGoalTrackerId[payload.goalTrackerId] = payload.data;
      }
    },
    [fetchGoalAnswers.rejected]: (state) => {
      state.loadingAnswers = false;
    },
    [createClientGoal.pending]: (state) => {
      state.loadingGoals = true;
    },
    [createClientGoal.fulfilled]: (state) => {
      state.loadingGoals = false;
    },
    [createClientGoal.rejected]: (state) => {
      state.loadingGoals = false;
    },
    [updateClientGoal.pending]: (state) => {
      state.loadingGoals = true;
    },
    [updateClientGoal.fulfilled]: (state) => {
      state.loadingGoals = false;
    },
    [updateClientGoal.rejected]: (state) => {
      state.loadingGoals = false;
    },
  },
});

export default goalTracker.reducer;

export const getClientGoalsPage = createSelector(
  (state) => state.goalTracker.goalsPage,
  (goalsPage) => goalsPage
);

export const getGoalAnswers = createSelector(
  (state) => state.goalTracker.answersByGoalTrackerId,
  (answersByGoalTrackerId) => answersByGoalTrackerId
);

export const getLoadingGoals = createSelector(
  (state) => state.goalTracker.loadingGoals,
  (loading) => loading
);

export const getLoadingAnswers = createSelector(
  (state) => state.goalTracker.loadingAnswers,
  (loading) => loading
);
